import { useEffect, useState } from "react";
import { Box, LinearProgress } from "@mui/material";

/** How long an image story stays on screen; videos run for their own length. */
export const STORY_DURATION_MS = 5000;

type Props = {
  totalStories: number;
  currentIndex: number;
  actorId: string;
  /** Progress restarts whenever the displayed story changes (activity ids are globally unique). */
  storyId: string;
  isPlaybackSuspended: boolean;
  isVideoStory: boolean;
  /**
   * Image stories only — false until the <img> actually loads, so the 5 s timer never runs
   * against a black frame on a slow connection. Videos self-gate via currentTime (always true).
   */
  isMediaReady: boolean;
  /** Read-only polling of currentTime/duration — the parent keeps element ownership and onEnded. */
  videoElement: HTMLVideoElement | null;
  /** Image stories only — fired once the timer completes. Videos advance via the parent's onEnded. */
  onAdvance: () => void;
};

/**
 * Owns the progress state and ALL of its ticking (10 Hz) so the fullscreen viewer tree doesn't
 * re-render on every tick — only this bar strip does.
 */
export const StoryProgressBars = ({
  totalStories,
  currentIndex,
  actorId,
  storyId,
  isPlaybackSuspended,
  isVideoStory,
  isMediaReady,
  videoElement,
  onAdvance,
}: Props) => {
  const [progress, setProgress] = useState(0);

  // Props-derived state reset: progress restarts in the same render pass that shows a new story,
  // so no frame ever pairs the previous story's progress with the new story.
  const resetKey = `${actorId}:${storyId}`;
  const [renderedResetKey, setRenderedResetKey] = useState(resetKey);
  if (renderedResetKey !== resetKey) {
    setRenderedResetKey(resetKey);
    setProgress(0);
  }

  // Image stories: fixed 5 s timer, held until the image has actually loaded (isMediaReady).
  // The updater stays pure (clamps at 100); advancing happens in the follow-up effect — calling
  // onAdvance inside the updater would double-advance under StrictMode's double-invocation in dev.
  useEffect(() => {
    if (isPlaybackSuspended || isVideoStory || !isMediaReady) {
      return;
    }

    const interval = setInterval(() => {
      setProgress((prev) =>
        Math.min(prev + 100 / (STORY_DURATION_MS / 100), 100),
      );
    }, 100);

    return () => clearInterval(interval);
  }, [isPlaybackSuspended, isVideoStory, isMediaReady]);

  // Image-only advance: video progress can hit 100 a tick before the element's onEnded fires —
  // advancing from here too would double-advance.
  useEffect(() => {
    if (progress >= 100 && !isPlaybackSuspended && !isVideoStory) {
      onAdvance();
    }
  }, [progress, isPlaybackSuspended, isVideoStory, onAdvance]);

  // Video stories: poll the element instead of an onTimeUpdate handler on the parent — the
  // per-story key={story.id} element remount changes the videoElement prop and restarts the
  // polling, and buffering naturally freezes the bar (currentTime stops advancing).
  useEffect(() => {
    if (isPlaybackSuspended || !isVideoStory || !videoElement) {
      return;
    }

    const interval = setInterval(() => {
      if (videoElement.duration > 0) {
        setProgress(
          Math.min(
            (videoElement.currentTime / videoElement.duration) * 100,
            100,
          ),
        );
      }
    }, 100);

    return () => clearInterval(interval);
  }, [isPlaybackSuspended, isVideoStory, videoElement]);

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 2,
        display: "flex",
        gap: 0.5,
        p: 1,
      }}
    >
      {Array.from({ length: totalStories }).map((_, i) => (
        <LinearProgress
          // storyId in the key: any story change (advance, back-tap) REMOUNTS the bars, so
          // value jumps like 60→0 or 0→100 snap instantly instead of animating backwards.
          key={`progress-${actorId}-${storyId}-${i}`}
          variant="determinate"
          value={i < currentIndex ? 100 : i === currentIndex ? progress : 0}
          sx={{
            flex: 1,
            height: 2,
            borderRadius: 1,
            bgcolor: "rgba(255,255,255,0.3)",
            "& .MuiLinearProgress-bar": {
              bgcolor: "white",
              // Matched to the 10 Hz tick interval so each tick glides into the next one —
              // MUI's default 0.4 s transition lags the ticks; none at all steps visibly.
              transition: "transform 100ms linear",
            },
          }}
        />
      ))}
    </Box>
  );
};
